<script lang="ts">
  import type { PictureSizes } from '@do/svelte-directus-images';

  import type { ItemWithOriginalSize, OriginalSize } from '@shared/types';
  import { cn } from '@shared/utils';

  import { getProductWrapperMaxWidthBasedOnArtworkHeight, mapOriginalSize } from '../libs/utils';

  //eslint-disable-next-line
  type ProductListItem = $$Generic<ItemWithOriginalSize>;
  type Styles = {
    wrapper: string;
    item: string;
    cardSizes: {
      [k: number]: string;
    };
  };
  export let products: ProductListItem[];
  export let wrapperClasses = '';

  const styles: Styles = {
    wrapper: 'flex flex-wrap items-center overflow-hidden justify-around -mx-3 lg:-mx-6 columns-auto gap-0',
    item: 'px-3 py-1 lg:px-6',
    cardSizes: {
      1: 'xl:w-3/12 md:w-4/12',
      2: 'xl:w-3/12 md:w-4/12',
      3: 'xl:w-3/12 md:w-4/12',
      4: 'xl:w-4/12 md:w-5/12',
      5: 'xl:w-5/12 md:w-6/12',
      6: 'xl:w-6/12 md:w-6/12',
      7: 'xl:w-6/12 md:w-6/12',
      8: 'xl:w-6/12 md:w-6/12',
      9: 'xl:w-6/12 md:w-10/12',
      10: 'xl:w-10/12 md:w-11/12',
      11: 'xl:w-11/12 md:w-full',
      12: 'md:w-full',
    },
  };

  const debug = false;
  const totalColumns = 12;
  const breakPoints: number[] = [0, 20, 40, 60, 80, 100, 120, 160, 220, 280, 360, +Infinity];

  const getProductWrapperMinWidthClassName = (cols: number): string => {
    const artworkMinWidthOfScreenWidthPercentage = 25; // set image min-width to 25% of screen width, but not larger than its container
    const widthOfScreen = cols / totalColumns;
    const countedVhWidth = Math.round(artworkMinWidthOfScreenWidthPercentage / widthOfScreen / 2) * 2;

    return `min-w-percent-${countedVhWidth > 100 ? 100 : countedVhWidth}`; // can not be wider than container (>100%)
  };

  const getProductClasses = (originalSize: OriginalSize, cols: number) => {
    return originalSize
      ? getProductWrapperMaxWidthBasedOnArtworkHeight(originalSize, true, 91)
      : '' + ' ' + getProductWrapperMinWidthClassName(cols);
  };

  const getProductsColSizes = (products: ItemWithOriginalSize[]): number[] => {
    const productsCols: number[] = [];
    const multiplier = 1;

    if (!products) return [];

    products.forEach((product) => {
      for (var i = 0; i < breakPoints.length; i++) {
        if (
          breakPoints[i] * multiplier >
          (mapOriginalSize(product?.original_size_x, product?.original_size_y)?.w ?? 100)
        ) {
          // if (breakPoints[i] * multiplier > (product?.originalsize?.w ?? 100)) {
          // add current breakpoint
          productsCols.push(i + 1); // set value from 1 to 12
          break;
        }
      }
    });

    return productsCols;
  };

  $: productSizes = getProductsColSizes(products); // reactive for recalc in case if 'load more' toggled

  const colsToBreakpointWidth = (cols: number): number => (cols >= 12 ? 100 : Math.ceil((100 / 12) * cols));

  $: getBreakpoints = (relativeColWidth: number): PictureSizes => {
    return {
      default: '720px',
      'min-1280': `calc(${colsToBreakpointWidth(relativeColWidth)}vw - 6rem)`,
      'min-1024': `calc(${colsToBreakpointWidth(relativeColWidth + 1)}vw - 4rem)`,
      'min-768': `calc(${colsToBreakpointWidth(relativeColWidth + 1)}vw - 4rem)`,
    };
  };
</script>

<ul class="{cn(styles.wrapper, { 'justify-start': products.length < 3 }, wrapperClasses)}">
  {#each products as product, index}
    <li
      class="{cn(
        'translate-z-0 relative mb-6 flex transform-gpu break-inside-avoid justify-center hover:z-10 lg:mb-12',
        styles.cardSizes[productSizes[index]],
        styles.item,
        {
          'md:w-1/2 lg:w-1/3': products.length < 3,
        },
      )}"
    >
      <div
        class="translate-z-0 w-full {getProductClasses(
          mapOriginalSize(product.original_size_x, product.original_size_y),
          productSizes[index],
        )}"
      >
        {#if debug}
          <div class="relative z-30">
            <code class="bg-default-200 absolute left-0 top-0 mb-2 block p-4 text-xs">
              <p class="text-xxs">x:{product.original_size_x}</p>
              <p class="text-xxs">y:{product.original_size_y}</p>
              <p>{styles.cardSizes[productSizes[index]]}</p>
              <p>cols: {productSizes[index]}</p>
            </code>
          </div>
        {/if}
        <slot {product} breakpoints="{getBreakpoints(productSizes[index])}" />
      </div>
    </li>
  {/each}
</ul>

<style lang="scss">
  //@media (min-width: 1024px) // eq to tailwind 'lg:' bp
  @media (min-width: 768px) {
    // eq to tailwind 'md:' bp
    @for $i from 10 through 150 {
      .max-w-vh-#{$i*2} {
        max-width: #{$i * 2}vh;
      }
    }

    @for $i from 10 through 50 {
      .min-w-percent-#{$i*2} {
        min-width: #{$i * 2} + '%';
      }
    }
  }
</style>
